import { useLayoutEffect, useRef } from 'react'
import { createPortal } from 'react-dom'

import { PacketDocument } from '@/components/chefs/print/packet/packet-document'
import type { PacketLayout } from '@/lib/chefs/packet/layout'
import { packetModelFor } from '@/lib/chefs/packet/model'
import type { PacketTarget } from '@/lib/chefs/packet/target'

/**
 * Renders the laid-out packet — the real print components, page numbers
 * included — into a hidden container and reports its markup: the input the
 * server turns into the print PDF. `hidden` + `print-screen-only` keep it off
 * the screen AND out of any browser print, so capturing never changes what a
 * system print outputs (only a mounted `.print-only` wrapper does that).
 */
export function PacketMarkupCapture({
  target,
  layout,
  onMarkup,
}: {
  target: PacketTarget
  layout: PacketLayout
  onMarkup: (markup: string) => void
}) {
  const ref = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    if (ref.current) {
      onMarkup(ref.current.innerHTML)
    }
  }, [target, layout, onMarkup])

  return createPortal(
    <div ref={ref} hidden aria-hidden className="print-screen-only">
      <PacketDocument model={packetModelFor(target)} layout={layout} preliminary={target.preliminary ?? null} />
    </div>,
    document.body,
  )
}

/**
 * The packet mounted for a PAGE print (browsers that cannot print a PDF in
 * place, i.e. Android Chrome). While it is mounted, the app's print CSS makes
 * it the only printable content.
 */
export function RetainedPrintPortal({ target, layout }: { target: PacketTarget; layout: PacketLayout }) {
  return createPortal(
    <div className="print-only">
      <PacketDocument model={packetModelFor(target)} layout={layout} preliminary={target.preliminary ?? null} />
    </div>,
    document.body,
  )
}
